import { cssDoModelo, ID_ESTILO_MODELO, type ModeloTema } from '@malgoos/shared';

/**
 * Estilos de interface por pessoa (27/10, ficha `demandas/entrada/2026-10-27_malgo_VIS_estilos-de-interface.md`). A Malgo é o
 * padrão e vale sem o atributo `data-estilo` no <html>; os outros trocam os tokens (src/estilos/estilos.css), os cantos e a
 * fonte do texto. Guardado em `malgoos.estilo` para o `public/tema.js` aplicar antes do primeiro paint.
 */

export const ESTILOS = ['mare_malgo', 'gelo', 'malgo', 'proprio', 'cupertino', 'mare', 'redmond', 'mountain_view', 'armonk', 'embarcadero', 'sydney', 'grafite', 'ottawa'] as const;
export type Estilo = (typeof ESTILOS)[number];
/** 03/11: a Maré Malgo é o padrão de quem não escolheu nada; a Malgo (modo atual) segue valendo sem o atributo no <html>. */
export const ESTILO_PADRAO: Estilo = 'mare_malgo';
export const ESTILO_SEM_ATRIBUTO: Estilo = 'malgo';

/**
 * Fonte de cada estilo (chave de nucleo/fontes.ts). Malgo, Maré, Gelo e modelo próprio não têm: vale a do workspace. Só fonte com licença que permite servir na web entra pelo app (Roboto,
 * DM Sans, IBM Plex Sans e Inter); a da Apple, da Microsoft, da Salesforce, da Atlassian e do GitHub é a do sistema.
 */
export const FONTE_DO_ESTILO: Partial<Record<Estilo, string>> = {
  cupertino: 'sistema',
  mare: 'dm_sans',
  redmond: 'sistema',
  mountain_view: 'roboto',
  armonk: 'plex_sans',
  embarcadero: 'sistema',
  sydney: 'sistema',
  grafite: 'sistema',
  ottawa: 'inter',
};

export const ESTILO_MODELO: Estilo = 'proprio';

export const ehEstilo = (v: unknown): v is Estilo => typeof v === 'string' && (ESTILOS as readonly string[]).includes(v);

/** rcor-3 ROB3-B3: entre sair e a próxima sessão, a prévia de Aparência não repõe o acento de quem saiu. */
let emSaida = false;
export const marcarSaida = () => {
  emSaida = true;
};
export const saidaEmCurso = () => emSaida;

/**
 * Aplica o estilo no <html> e guarda para o `tema.js`. Valor desconhecido vale o padrão. A Malgo fica sem o atributo, mas agora
 * é guardada: sem chave, o `tema.js` aplica o padrão (Maré Malgo), e quem escolheu o modo atual não pode piscar nele.
 */
export function aplicarEstilo(estilo: string | null | undefined) {
  emSaida = false;
  const e: Estilo = ehEstilo(estilo) ? estilo : ESTILO_PADRAO;
  const raiz = document.documentElement;
  // Só escreve quando muda: o tema.js já pôs o mesmo valor antes do paint.
  if (e === ESTILO_SEM_ATRIBUTO) {
    if (raiz.dataset.estilo !== undefined) delete raiz.dataset.estilo;
  } else if (raiz.dataset.estilo !== e) raiz.dataset.estilo = e;
  try {
    localStorage.setItem('malgoos.estilo', e);
  } catch {
    /* sem storage */
  }
}

/**
 * Modelo próprio (03/11): as cores do modelo ativo num <style> só dele, depois de validadas em `cssDoModelo`, e guardadas para o
 * `tema.js` repor antes do paint. Sem modelo (ou em outro estilo), tira os dois.
 */
export function aplicarModelo(modelo: Pick<ModeloTema, 'claro' | 'escuro'> | null) {
  const css = modelo ? cssDoModelo(modelo) : '';
  let el = document.getElementById(ID_ESTILO_MODELO);
  if (css) {
    if (!el) {
      el = document.createElement('style');
      el.id = ID_ESTILO_MODELO;
      document.head.appendChild(el);
    }
    if (el.textContent !== css) el.textContent = css;
  } else el?.remove();
  try {
    if (css && modelo) localStorage.setItem('malgoos.modelo', JSON.stringify({ claro: modelo.claro, escuro: modelo.escuro }));
    else localStorage.removeItem('malgoos.modelo');
  } catch {
    /* sem storage */
  }
}
